import { useEffect, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { isAuthenticated } from '@/lib/auth';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { bootstrapAuth, selectIsAuthenticated } from './authSlice';

/**
 * Route guard (design §5.3 `RequireAuth`; named `ProtectedRoute` per the plan).
 * Unauthenticated visits redirect to `/login`, remembering where the user was
 * headed so the login page can put them back there afterwards.
 *
 * Used as a layout element, so it renders `<Outlet/>` by default; passing
 * `children` wraps a specific element instead.
 */
export function ProtectedRoute({ children }: { children?: ReactNode }) {
  const dispatch = useAppDispatch();
  const authenticated = useAppSelector(selectIsAuthenticated);
  const location = useLocation();

  // Hydrates the slice from storage and refreshes proactively when the access
  // token is about to expire (design §13.1).
  useEffect(() => {
    dispatch(bootstrapAuth());
  }, [dispatch]);

  if (!authenticated && !isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
